// Splits a message's text into plain runs and web addresses, so the
// addresses can be shown as links. A message is still never treated as
// HTML: every run goes in as a text node, and a link's address is only ever
// an http(s) one that appeared in the text itself.

export interface TextRun {
  text: string;
  // Present when this run is an address to link.
  href?: string;
}

// An address runs until whitespace or a character that cannot be part of
// one. Arabic letters are allowed: this store's own product addresses are
// written with them.
const ADDRESS = /https?:\/\/[^\s<>"'`]+/g;

// Punctuation that ends a sentence rather than an address — "see
// https://flix-store.com/collections/alerts." links without the full stop.
const TRAILING = /[.,!?;:،؛؟)\]}»"']+$/;

export function splitLinks(text: string): TextRun[] {
  const runs: TextRun[] = [];
  let last = 0;

  for (const match of text.matchAll(ADDRESS)) {
    const start = match.index ?? 0;
    const trailing = TRAILING.exec(match[0])?.[0] ?? '';
    const address = trailing ? match[0].slice(0, -trailing.length) : match[0];

    // "https://" on its own, or with only punctuation after it, is not a link.
    if (!/^https?:\/\/[^/]/.test(address)) {
      continue;
    }

    if (start > last) {
      runs.push({ text: text.slice(last, start) });
    }

    runs.push({ text: address, href: address });
    last = start + address.length;
  }

  if (last < text.length) {
    runs.push({ text: text.slice(last) });
  }

  return runs;
}
